/**
 * Inserir um trecho da Bíblia no texto.
 *
 * Três modos, porque três são as formas de citar num sermão ou num estudo:
 *
 * 1. **só a referência** — para citar no meio da frase;
 * 2. **o texto e, depois, a referência** — o versículo corrido e a fonte na linha
 *    de baixo, como se escreve à mão;
 * 3. **a referência no texto e o trecho numa caixa cinza abaixo** — a frase segue
 *    limpa e o versículo aparece embaixo, em itálico sobre fundo cinza.
 *
 * Sobre a versão: só entra tradução em **domínio público**. Hoje é a *João
 * Ferreira de Almeida*, servida pela API pública; NVI, ARA, NTLH e ACF são
 * protegidas por direito autoral e não são distribuídas aqui. O nome da versão
 * aparece na referência, para o leitor do documento saber o que está lendo.
 */

import { useState } from 'react';
import { BookOpen, Loader2, Plus, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Campo } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import {
  DESCRICAO_MODO_DE_INSERCAO,
  MODOS_DE_INSERCAO_BIBLICA,
  ROTULO_MODO_DE_INSERCAO,
  htmlDaInsercaoBiblica,
  referenciaBiblica,
  textoDoTrecho,
} from '@miolos/core';
import type { ModoDeInsercaoBiblica } from '@miolos/core';
import { api, ErroApi, type TrechoDaBiblia } from '@/lib/api';

export function InserirBiblia({ aoInserir }: { aoInserir: (html: string) => void }) {
  const [aberto, setAberto] = useState(false);
  const [referencia, setReferencia] = useState('');
  const [resultado, setResultado] = useState<TrechoDaBiblia | null>(null);
  const [modo, setModo] = useState<ModoDeInsercaoBiblica>('CAIXA_ABAIXO');
  const [buscando, setBuscando] = useState(false);
  const [erro, setErro] = useState('');

  const buscar = async () => {
    if (!referencia.trim()) {
      setErro('Escreva a referência (ex.: 1 Co 15:21-24).');
      return;
    }
    setBuscando(true);
    setErro('');
    try {
      setResultado(await api.trechoDaBiblia(referencia.trim()));
    } catch (causa) {
      setResultado(null);
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível consultar agora.');
    } finally {
      setBuscando(false);
    }
  };

  const inserir = () => {
    if (!resultado) return;
    aoInserir(htmlDaInsercaoBiblica(resultado.trecho, modo, resultado.versao));
    setAberto(false);
    setReferencia('');
    setResultado(null);
  };

  return (
    <Dialog open={aberto} onOpenChange={setAberto}>
      <DialogTrigger asChild>
        <Button variant="contorno" tamanho="pequeno">
          <BookOpen />
          Bíblia
        </Button>
      </DialogTrigger>
      <DialogContent
        titulo="Inserir trecho da Bíblia"
        descricao="Versão João Ferreira de Almeida, em domínio público. Escreva a referência no formato “1 Co 15:21-24” ou “João 3:16”."
      >
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[200px] flex-1">
              <Campo rotulo="Referência">
                <input
                  value={referencia}
                  onChange={(evento) => setReferencia(evento.target.value)}
                  onKeyDown={(evento) => {
                    if (evento.key === 'Enter') {
                      evento.preventDefault();
                      void buscar();
                    }
                  }}
                  placeholder="1 Co 15:21-24"
                  className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                />
              </Campo>
            </div>
            <Button variant="contorno" onClick={() => void buscar()} disabled={buscando}>
              {buscando ? <Loader2 className="animate-spin" /> : <BookOpen />}
              Buscar
            </Button>
          </div>

          {erro ? (
            <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
              {erro}
            </p>
          ) : null}

          {resultado ? (
            <>
              <div className="space-y-1 rounded-md border border-border bg-muted/40 p-2">
                <p className="text-xs font-medium">{referenciaBiblica(resultado.trecho, resultado.versao)}</p>
                <p className="text-xs leading-relaxed text-muted-foreground">{textoDoTrecho(resultado.trecho)}</p>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-medium">Como inserir</p>
                {MODOS_DE_INSERCAO_BIBLICA.map((opcao) => (
                  <label
                    key={opcao}
                    className="flex cursor-pointer items-start gap-2 rounded-md border border-border p-2 text-xs leading-relaxed hover:border-primary/40"
                  >
                    <input
                      type="radio"
                      name="modo-biblia"
                      checked={modo === opcao}
                      onChange={() => setModo(opcao)}
                      className="mt-0.5"
                    />
                    <span>
                      <strong>{ROTULO_MODO_DE_INSERCAO[opcao]}</strong>
                      <br />
                      <span className="text-muted-foreground">{DESCRICAO_MODO_DE_INSERCAO[opcao]}</span>
                    </span>
                  </label>
                ))}
              </div>

              <Button onClick={inserir}>
                <Plus />
                Inserir no texto
              </Button>
            </>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
